/**
 * Contract types for the photo-moderation reviewer tool's three endpoints
 * (queue / decide / enhance), plus the rejection-reason enum.
 *
 * These are the FE-side source of truth until `@owner/olympus-client` is
 * regenerated with the reviewer endpoints (GUE-3888), after which the request /
 * response shapes are replaced by the generated client types.
 *
 * Storage model: guest photos live in the shared public CDN bucket (written via
 * `ImageService.upsertImage`) — `original` / `enhanced` are public URLs, not
 * presigned. Aligned with the GUE-3816 entity, which stores only
 * `GuestPhotoImages { original, enhanced? }` (no derivative / thumbnail).
 * `status` mirrors GUE-3816's enum; the reward arm there is `reward300 | goodwill`.
 */

/** Single-source-of-truth Unleash flag name for the photo reviewer tool. */
export const GUEST_PHOTO_REVIEWER_FLAG = 'hermes.guest-photo-reviewer'

/**
 * Flag gating the reviewer "enhance" action (reuses the AI photo-quality
 * pipeline). Should align with the backend enhance endpoint's gate.
 */
export const GUEST_PHOTO_ENHANCE_FLAG = 'hermes.guest-photo-enhance'

/**
 * Submission lifecycle status — mirrors GUE-3816's `GuestPhotoSubmissionStatus`.
 * The reviewer queue only surfaces `pending`; `accepted` / `rejected` are the
 * terminal states a decision writes.
 */
export enum GuestPhotoSubmissionStatus {
  Pending = 'pending',
  Accepted = 'accepted',
  Rejected = 'rejected',
}

/** Reviewer rejection reasons — Postgres-native enum, 8 v1 values (RQ7). */
export enum GuestPhotoRejectionReason {
  QualityBlurry = 'quality_blurry',
  QualityLighting = 'quality_lighting',
  QualityPartial = 'quality_partial',
  ContentWrongItem = 'content_wrong_item',
  ContentInappropriate = 'content_inappropriate',
  ContentFraud = 'content_fraud',
  ContentPii = 'content_pii',
  Other = 'other',
}

/** A single pending submission as shown in the queue + detail views. */
export interface ReviewSubmissionSummary {
  submissionId: string
  userId: string
  userEmail: string
  orderId: string
  orderItemId: string
  /** Public CDN URL of the uploaded photo (`GuestPhotoImages.original`). */
  originalUrl: string
  /**
   * Public CDN URL of the AI-enhanced candidate (`GuestPhotoImages.enhanced`).
   * Null until the reviewer runs `enhance` and the workflow writes it back
   * (the UI polls for this).
   */
  enhancedUrl: string | null
  submittedAt: string
  itemDescription: string
  modifiers: string[]
}

/** An item with its 1-2 pending submissions, grouped under a brand. */
export interface ReviewQueueItem {
  menuItemId: string
  menuItemName: string
  locationId: string
  locationName: string
  pendingSubmissions: ReviewSubmissionSummary[]
}

/** A brand bucket in the queue, sorted by oldest-pending age. */
export interface ReviewQueueBrand {
  brandId: string
  brandName: string
  oldestPendingAt: string
  items: ReviewQueueItem[]
}

/** Response for `GET /guestPhotos/v1/reviewer/queue`. */
export interface ReviewQueueResponse {
  brands: ReviewQueueBrand[]
}

/** Which variant to publish when accepting an (optionally) enhanced submission. */
export enum PublishRef {
  Original = 'original',
  Enhanced = 'enhanced',
}

/** Accept payload from the reviewer UI: which submission + which variant to publish. */
export interface ReviewAcceptPayload {
  submissionId: string
  publishRef?: PublishRef
}

/** Request body for `POST /guestPhotos/v1/reviewer/{id}/decide`. */
export type ReviewDecisionRequest =
  // When an enhanced variant exists the reviewer UI always sends an explicit
  // publishRef; an omitted publishRef therefore means there is no enhanced
  // candidate and the server publishes `original`.
  | { action: 'accept'; publishRef?: PublishRef }
  | { action: 'reject'; reasonCode: GuestPhotoRejectionReason; note?: string }
  | { action: 'unsure'; note?: string }

/**
 * Response for `POST /guestPhotos/v1/reviewer/{id}/enhance` (HTTP 202 — the
 * enhancement runs async; poll the queue/detail for `enhancedUrl`).
 */
export interface ReviewEnhanceResponse {
  enhancementId: string
}
